import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  MapPinIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  HomeIcon,
  AcademicCapIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import ArticleSchema from '@/components/seo/ArticleSchema';
import PSEODisclaimer from '@/components/seo/PSEODisclaimer';
import { fetchStateEntry } from '@/services/pseoService';

const BASE_URL = 'https://seniorthrive.com';

/**
 * State-level pSEO detail page. Scope is strictly informational with .gov citations.
 * Every factual claim must have a source link. Prescriptive language is banned by
 * the scripts/pseo-lint.mjs linter. See .claude/plans/silly-dreaming-knuth.md.
 */
const StatePage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: entry, isLoading, error } = useQuery({
    queryKey: ['pseo-state', slug],
    queryFn: () => fetchStateEntry(slug!),
    enabled: !!slug,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  if (isLoading) {
    const stateName = slug
      ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'State';

    return (
      <>
        <SEO
          title={`Aging in Place in ${stateName}: Resources and Programs`}
          description={`Aging-in-place programs, Medicaid waivers, Area Agencies on Aging, and tax benefits in ${stateName}. Sourced from official state and federal agencies.`}
          canonical={`${BASE_URL}/states/${slug}`}
        />
        <div className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Skeleton className="h-4 w-24 mb-4" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-4 w-48 mb-8" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !entry) {
    return (
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">State Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The state resource page you're looking for doesn't exist yet or has been moved.
          </p>
          <Button asChild>
            <Link to="/states">
              <ArrowLeftIcon className="mr-2 w-4 h-4" />
              Browse All States
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const pageUrl = `${BASE_URL}/states/${entry.slug}`;
  const metaTitle =
    entry.seo_meta?.metaTitle || `Aging in Place in ${entry.state_name}: Resources and Programs`;
  const metaDescription =
    entry.seo_meta?.metaDescription ||
    `Aging-in-place programs, Medicaid waivers, Area Agencies on Aging, and tax benefits in ${entry.state_name}. Sourced from official state and federal agencies.`;
  const metaKeywords = entry.seo_meta?.keywords?.join(', ') || '';

  const breadcrumbs = [
    { name: 'Home', url: BASE_URL },
    { name: 'State Resources', url: `${BASE_URL}/states` },
    { name: entry.state_name, url: pageUrl },
  ];

  const primarySourceUrl = entry.hcbs_waiver?.source_url || entry.hcbs_waiver?.eligibility_url;
  const primarySourceLabel = entry.hcbs_waiver?.name
    ? `${entry.state_name} Medicaid (${entry.hcbs_waiver.name})`
    : `${entry.state_name} state Medicaid agency`;

  // Structured data: GovernmentService for each named program so Google can surface
  // the authoritative nature of the content.
  const schemaItems = [] as object[];
  if (entry.hcbs_waiver?.name) {
    schemaItems.push({
      '@type': 'GovernmentService',
      name: entry.hcbs_waiver.name,
      serviceType: 'Home and Community-Based Services Waiver',
      provider: {
        '@type': 'GovernmentOrganization',
        name: `${entry.state_name} Medicaid`,
      },
      ...(entry.hcbs_waiver.source_url && { url: entry.hcbs_waiver.source_url }),
      ...(entry.hcbs_waiver.description && { description: entry.hcbs_waiver.description }),
    });
  }
  if (entry.fall_prevention?.program_name) {
    schemaItems.push({
      '@type': 'GovernmentService',
      name: entry.fall_prevention.program_name,
      serviceType: 'Fall Prevention Program',
      ...(entry.fall_prevention.agency && {
        provider: { '@type': 'GovernmentOrganization', name: entry.fall_prevention.agency },
      }),
      ...(entry.fall_prevention.url && { url: entry.fall_prevention.url }),
      ...(entry.fall_prevention.description && { description: entry.fall_prevention.description }),
    });
  }

  const itemListSchema =
    schemaItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: schemaItems.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item,
          })),
        }
      : null;

  const SourceLink = ({ url, label }: { url?: string; label?: string }) => {
    if (!url) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm text-primary underline hover:no-underline"
      >
        {label || 'Source'}
        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" aria-hidden="true" />
      </a>
    );
  };

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
        url={pageUrl}
        canonical={pageUrl}
        type="article"
        breadcrumbs={breadcrumbs}
        article={{
          publishedTime: entry.published_at || entry.created_at,
          modifiedTime: entry.updated_at,
          author: 'SeniorThrive Team',
          section: 'State Resources',
        }}
      />
      <ArticleSchema
        title={metaTitle}
        description={metaDescription}
        url={pageUrl}
        authorName="SeniorThrive Team"
        datePublished={entry.published_at || entry.created_at}
        dateModified={entry.updated_at}
      />
      {itemListSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
        />
      )}

      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <Button variant="ghost" asChild>
              <Link to="/states">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                All States
              </Link>
            </Button>
          </div>

          <article>
            <header className="mb-10">
              <Badge variant="secondary" className="mb-4">
                <MapPinIcon className="w-3.5 h-3.5 mr-1" />
                {entry.region}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
                Aging in Place in {entry.state_name}
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Resources, programs, and benefits for older adults and families.
              </p>
            </header>

            {entry.overview && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Overview</h2>
                <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">
                  {entry.overview}
                </p>
                {entry.demographics?.source_url && (
                  <p className="mt-3">
                    <SourceLink url={entry.demographics.source_url} label="Census / ACL profile" />
                  </p>
                )}
              </section>
            )}

            {entry.hcbs_waiver?.name && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <BuildingLibraryIcon className="w-6 h-6" aria-hidden="true" />
                  {entry.state_name} Medicaid HCBS Waiver
                </h2>
                <div className="space-y-3">
                  <p className="text-foreground text-lg">
                    <strong>Program:</strong> {entry.hcbs_waiver.name}
                  </p>
                  {entry.hcbs_waiver.description && (
                    <p className="text-muted-foreground leading-relaxed">
                      {entry.hcbs_waiver.description}
                    </p>
                  )}
                  {entry.hcbs_waiver.coverage_summary && (
                    <p className="text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">What it may cover:</strong>{' '}
                      {entry.hcbs_waiver.coverage_summary}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 pt-2">
                    {entry.hcbs_waiver.eligibility_url && (
                      <SourceLink
                        url={entry.hcbs_waiver.eligibility_url}
                        label="Eligibility details"
                      />
                    )}
                    {entry.hcbs_waiver.application_url && (
                      <SourceLink url={entry.hcbs_waiver.application_url} label="How to apply" />
                    )}
                  </div>
                </div>
              </section>
            )}

            {entry.area_agencies?.count !== undefined && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <UserGroupIcon className="w-6 h-6" aria-hidden="true" />
                  Area Agencies on Aging
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg mb-3">
                  {entry.state_name} has <strong>{entry.area_agencies.count}</strong> Area Agencies
                  on Aging (AAAs). AAAs connect older adults and families to local services like
                  meal programs, transportation, caregiver support, and benefits counseling.
                </p>
                {entry.area_agencies.directory_url && (
                  <p>
                    <SourceLink
                      url={entry.area_agencies.directory_url}
                      label={`Find your ${entry.state_name} AAA`}
                    />
                  </p>
                )}
                {entry.area_agencies.notable_orgs && entry.area_agencies.notable_orgs.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {entry.area_agencies.notable_orgs.map((org) => (
                      <li key={org.url} className="text-muted-foreground">
                        <SourceLink url={org.url} label={org.name} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {entry.tax_programs?.programs && entry.tax_programs.programs.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <CurrencyDollarIcon className="w-6 h-6" aria-hidden="true" />
                  Senior Tax Benefits
                </h2>
                <ul className="space-y-4">
                  {entry.tax_programs.programs.map((prog) => (
                    <li key={prog.url} className="border-l-2 border-border pl-4">
                      <p className="font-bold text-foreground">{prog.name}</p>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-1">
                        {prog.description}
                      </p>
                      <SourceLink url={prog.url} label="Program details" />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {entry.fall_prevention?.program_name && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <ShieldCheckIcon className="w-6 h-6" aria-hidden="true" />
                  Fall Prevention Program
                </h2>
                <p className="text-foreground text-lg mb-2">
                  <strong>{entry.fall_prevention.program_name}</strong>
                  {entry.fall_prevention.agency && (
                    <span className="text-muted-foreground"> · {entry.fall_prevention.agency}</span>
                  )}
                </p>
                {entry.fall_prevention.description && (
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    {entry.fall_prevention.description}
                  </p>
                )}
                {entry.fall_prevention.url && (
                  <SourceLink url={entry.fall_prevention.url} label="Program page" />
                )}
              </section>
            )}

            {entry.pace_programs?.available !== undefined && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <HomeIcon className="w-6 h-6" aria-hidden="true" />
                  PACE (Program of All-Inclusive Care for the Elderly)
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg mb-3">
                  {entry.pace_programs.available
                    ? `PACE is available in ${entry.state_name}. PACE provides comprehensive medical and social services to eligible older adults who want to stay in their community.`
                    : `PACE is not currently available in ${entry.state_name}. Other Medicaid-funded options may be available.`}
                </p>
                {entry.pace_programs.notes && (
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    {entry.pace_programs.notes}
                  </p>
                )}
                {entry.pace_programs.find_url && (
                  <SourceLink
                    url={entry.pace_programs.find_url}
                    label="Find a PACE organization"
                  />
                )}
              </section>
            )}

            {entry.ot_licensing?.board_url && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AcademicCapIcon className="w-6 h-6" aria-hidden="true" />
                  Occupational Therapy Licensing
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  Occupational therapists licensed in {entry.state_name} can evaluate home safety
                  and recommend modifications for aging in place. Verify a practitioner's license
                  with the state board.
                </p>
                <SourceLink
                  url={entry.ot_licensing.board_url}
                  label={entry.ot_licensing.board_name || `${entry.state_name} OT Board`}
                />
              </section>
            )}

            {entry.related_state_slugs.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Nearby States</h2>
                <div className="flex flex-wrap gap-2">
                  {entry.related_state_slugs.map((s) => (
                    <Link
                      key={s}
                      to={`/states/${s}`}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors"
                    >
                      {s.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="bg-gradient-to-r from-blue-50 to-teal-50 dark:from-blue-950/30 dark:to-teal-950/30 rounded-xl p-8 text-center border border-blue-100 dark:border-blue-900">
              <h2 className="text-2xl font-bold text-foreground mb-3">
                Make Your Home Work for You
              </h2>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                SeniorThrive's Home Safety Check identifies risks room by room and matches them to
                local occupational therapists and contractors. Free to try.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg">
                  <a
                    href={`https://app.seniorthrive.com/auth?signup=true&utm_source=states&utm_medium=pseo&utm_campaign=${entry.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Start Your Free Trial
                  </a>
                </Button>
                <Button variant="outline" asChild size="lg">
                  <Link to="/professionals/directory">Find a Professional</Link>
                </Button>
              </div>
            </section>
          </article>

          <PSEODisclaimer
            lastUpdated={entry.updated_at}
            primarySourceUrl={primarySourceUrl}
            primarySourceLabel={primarySourceLabel}
            className="mt-12"
          />

          <div className="border-t pt-8 mt-12">
            <Button asChild>
              <Link to="/states">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                Back to All States
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatePage;
